// Attendance tab — mark sessions, schedule recurring, migrate history.
import { useState, useEffect, useRef } from "react";
import { doc, setDoc, deleteDoc, getDocs, collection, writeBatch } from "firebase/firestore";
import { db } from "../lib/firebase";
import { syncCol } from "../lib/sync";
import { addDays, canMarkAttendance, isSessionBased, normalizePackage, packageExpiryOf, sessionsTotalOf } from "../domain";
import { uid, sl, cl } from "../lib/format";
import { getActivePkg, markCounterPatch } from "../lib/packages";
import { themeTokens } from "../lib/theme";
import { clientToday } from "../domain";
import { SD, Inp, Btn, Icon, Empty, AddTimeBtn, ConfirmButton, useToast } from "../ui/primitives";

export const Attendance=({clients,setClients,applyClientAndPayment,theme,userId,profile,registerBack,onRenewNeeded,extRecords,setExtRecords})=>{
  const toast=useToast();
  const [sel,setSel]=useState("");
  // Seed from the root state so there's no empty flash on tab switch, and so
  // records still populate even on the rare tick where syncCol's own fetch
  // below is a no-op (its dedup check: rows byte-identical to the LS cache).
  const [records,setRecords]=useState(()=>[...(extRecords||[])].sort((a,b)=>(b.date||'').localeCompare(a.date||'')));
  const [bulkMode,setBulkMode]=useState(false);
  useEffect(()=>{
    if(!registerBack)return;
    if(sel)registerBack("attendance-sel",()=>setSel(""));
    else registerBack("attendance-sel",null);
  },[sel,registerBack]);
  const [bulkSelected,setBulkSelected]=useState([]);
  const [bulkDate,setBulkDate]=useState(clientToday());
  const [showBulkTime,setShowBulkTime]=useState(false);
  const [bulkTime,setBulkTime]=useState("");
  useEffect(()=>{
    if(!userId)return;
    // syncCol is the same offline-safe helper App.js uses for this collection —
    // a raw getDocs() here skipped its guard against an empty offline snapshot
    // clobbering already-good records.
    syncCol(userId,"attendance",(rows)=>{
      const recs=[...rows].sort((a,b)=>(b.date||'').localeCompare(a.date||''));
      setRecords(recs);
      if(setExtRecords)setExtRecords(recs);
    },false);
  },[userId]);
  
  // Once per session: migrate any legacy attendanceHistory array into the
  // attendance collection. Counters are updated incrementally at each mark/delete
  // — never recomputed from a snapshot (an offline-empty snapshot would zero them).
  const migratedRef=useRef(false);
  useEffect(()=>{
    if(!userId||!clients?.length||migratedRef.current)return;
    migratedRef.current=true;
    const migrate=async()=>{
      let snap=await getDocs(collection(db,"users",userId,"attendance"));
      const existingIds=new Set(snap.docs.map(d=>d.id));
      const toMigrate=[];
      for(const c of clients){
        for(const h of (c.attendanceHistory||[])){
          if(h.id&&!existingIds.has(h.id)){
            toMigrate.push({id:h.id,clientId:c.id,clientName:c.name,date:h.date||"",time:h.time||"",note:h.note||""});
          }
        }
      }
      if(toMigrate.length>0){
        for(const r of toMigrate){
          await setDoc(doc(db,"users",userId,"attendance",r.id),{clientId:r.clientId,clientName:r.clientName,date:r.date,time:r.time||"",note:r.note||""});
        }
        snap=await getDocs(collection(db,"users",userId,"attendance"));
      }
      const recs=snap.docs.map(d=>({id:d.id,...d.data()}))
        .sort((a,b)=>(b.date||'').localeCompare(a.date||''));
      setRecords(recs);
      if(setExtRecords)setExtRecords(recs);
    };
    migrate().catch(e=>console.warn("Migration error:",e));
  },[userId,clients]);
  // When shri marks attendance - it adds to extRecords - merge into local records
  useEffect(()=>{
    if(!extRecords || extRecords.length === 0) return;
    setRecords(prev=>{
      const existingIds = new Set(prev.map(r=>r.id));
      const newOnes = extRecords.filter(r=>!existingIds.has(r.id));
      if(newOnes.length === 0) return prev;
      return [...newOnes, ...prev].sort((a,b)=>(b.date||'').localeCompare(a.date||''));
    });
  },[extRecords]);
  const [note,setNote]=useState("");
  const [attDate,setAttDate]=useState(clientToday());
  const [attTime,setAttTime]=useState("");
  const {isDark,cb,bc,tc,mc}=themeTokens(theme);

  const toggleBulkClient=(id)=>setBulkSelected(prev=>prev.includes(id)?prev.filter(x=>x!==id):[...prev,id]);
  const markBulk=()=>{
    if(bulkSelected.length===0)return;
    const okRows=[];const skipped=[];
    for(const cid of bulkSelected){
      const c=clients.find(x=>x.id===cid);if(!c)continue;
      const g=canMarkAttendance(c,bulkDate,{existingForClient:records,time:bulkTime,now:new Date()});
      if(!g.ok){skipped.push(c.name);continue;}
      okRows.push({id:uid(),clientId:cid,clientName:c.name,date:bulkDate,time:bulkTime||"",note:"Bulk attendance"});
    }
    if(okRows.length===0){toast(`Nothing to mark${skipped.length?` — ${skipped.length} skipped (future / expired / no sessions / already marked)`:""}.`,"warning");return;}
    // 1. optimistic local state — survives offline
    const okIds=new Set(okRows.map(r=>r.clientId));
    setRecords(nr=>[...okRows,...nr]);
    setClients(prev=>prev.map(x=>okIds.has(x.id)?{...x,...markCounterPatch(x)}:x));
    toast(`${okRows.length} marked${skipped.length?` · ${skipped.length} skipped`:""}`,skipped.length?"warning":"success");
    setBulkSelected([]);setBulkMode(false);
    // 2. fire the batch — Firestore buffers it offline, syncs on reconnect
    if(userId){
      const b=writeBatch(db);
      okRows.forEach(r=>{const{id,...d}=r;b.set(doc(db,"users",userId,"attendance",id),d);});
      b.commit().catch(e=>{console.error("Bulk attendance sync failed:",e);toast("Some attendance didn't sync — it'll retry when you're online.","warning");});
    }
  };

  const saveRec=async(rec)=>{
    const {id,...data}=rec;
    if(userId){
      try{await setDoc(doc(db,"users",userId,"attendance",id),data);}
      catch(e){console.error("Attendance save failed:",e);toast("Couldn't save attendance — check your connection and try again.","error");}
    }
  };
  // Counter writes go through the App-level setClients, which persists the full
  // client doc (counters included) — so the increment survives a resync.
  const bumpCounter=(id)=>setClients(prev=>prev.map(x=>x.id===id?{...x,...markCounterPatch(x)}:x));

  const mark=()=>{
    if(!sel)return;
    const c=clients.find(x=>x.id===sel);
    if(!c)return;
    const g=canMarkAttendance(c,attDate,{existingForClient:records,time:attTime,now:new Date()});
    if(!g.ok){
      if(g.reason==="no-sessions"||g.reason==="expired"){if(onRenewNeeded)onRenewNeeded(c);return;}
      if(g.reason==="future"){toast("Cannot mark attendance for a future date.","warning");return;}
      toast(`Attendance already marked for ${c.name} on ${attDate}${attTime?` at ${attTime}`:""}.`,"warning");return;
    }
    const rec={id:uid(),clientId:sel,clientName:c.name,date:attDate,time:attTime,note};
    setRecords(nr=>[rec,...nr]);saveRec(rec);
    bumpCounter(sel);
    toast("Attendance marked ✓","success");
    setNote("");
  };

  const markSlot=(slot)=>{
    const c=clients.find(x=>x.id===sel);
    if(!c||!slot.time)return;
    const g=canMarkAttendance(c,attDate,{existingForClient:records,time:slot.time,now:new Date()});
    if(!g.ok){
      if(g.reason==="no-sessions"||g.reason==="expired"){if(onRenewNeeded)onRenewNeeded(c);return;}
      if(g.reason==="future"){toast("Cannot mark attendance for a future date.","warning");return;}
      toast(`Already marked for ${slot.time} on ${attDate}.`,"warning");return;
    }
    const rec={id:uid(),clientId:sel,clientName:c.name,date:attDate,time:slot.time,note:"Scheduled class"};
    setRecords(nr=>[rec,...nr]);saveRec(rec);
    bumpCounter(sel);
    toast("Attendance marked ✓","success");
  };

  const client=clients.find(c=>c.id===sel);
  const history=records.filter(r=>r.clientId===sel)
    .sort((a,b)=>(b.date||'').localeCompare(a.date||'')||(b.time||'').localeCompare(a.time||''));
  const deleteRec=(id)=>{
    const rec=records.find(r=>r.id===id);
    setRecords(prev=>prev.filter(r=>r.id!==id));
    if(userId)deleteDoc(doc(db,"users",userId,"attendance",id)).catch(e=>console.error("Attendance delete failed:",e));
    const cl=clients.find(x=>x.id===sel);
    if(cl&&rec){
      // only touch the current package's counters if the deleted record belongs
      // to it (after a renew, the history list also shows prior-package records)
      const start=normalizePackage(cl)?.startDate||null;
      const belongsToCurrent=!start||(rec.date||'')>=start;
      if(belongsToCurrent){
        const total=sessionsTotalOf(cl);
        const patch={
          classesAttended:Math.max(0,(Number(cl.classesAttended)||0)-1),
          classesRemaining:isSessionBased(cl)
            ?(total>0?Math.min(total,(Number(cl.classesRemaining)||0)+1):(Number(cl.classesRemaining)||0)+1)
            :cl.classesRemaining,
        };
        setClients(prev=>prev.map(x=>x.id===sel?{...x,...patch}:x)); // App-level setClients persists the client doc
      }
    }
    toast("Attendance record deleted","info");
  };

  const fillSlot=(slot)=>{
    if(slot.time)setAttTime(slot.time);
  };

  return(
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-bold" style={{color:tc}}>Attendance</h2>
        <button onClick={()=>{setBulkMode(!bulkMode);setBulkSelected([]);}}
          className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold border transition-all"
          style={{background:bulkMode?(theme?.primary||"#0f766e"):"transparent",color:bulkMode?"#fff":(theme?.primary||"#0f766e"),borderColor:theme?.primary||"#0f766e"}}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="9 11 12 14 22 4"/><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/></svg>
          {bulkMode?"Cancel Bulk":"Mark Multiple"}
        </button>
      </div>

      {/* BULK MODE */}
      {bulkMode&&(
        <div className="rounded-2xl border p-4 space-y-3" style={{background:cb,borderColor:bc}}>
          <p className="text-sm font-bold" style={{color:tc}}>Select clients to mark present</p>
          <div className="flex items-end gap-3">
            <div className="flex flex-col gap-1 flex-1">
              <label className="text-xs font-semibold uppercase tracking-wider" style={{color:mc}}>Date</label>
              <input type="date" max={clientToday()} className="border rounded-xl px-3 py-2 text-sm focus:outline-none"
                style={{background:isDark?"#0f172a":"#f9fafb",borderColor:bc,color:tc}}
                value={bulkDate} onChange={e=>setBulkDate(e.target.value)}/>
            </div>
            {showBulkTime
              ?<div className="flex flex-col gap-1">
                <label className="text-xs font-semibold uppercase tracking-wider" style={{color:mc}}>Time</label>
                <input type="time" className="border rounded-xl px-3 py-2 text-sm focus:outline-none"
                  style={{background:isDark?"#0f172a":"#f9fafb",borderColor:bc,color:tc}}
                  value={bulkTime} onChange={e=>setBulkTime(e.target.value)}/>
              </div>
              :<AddTimeBtn onClick={()=>setShowBulkTime(true)} theme={theme}/>
            }
          </div>
          <div className="max-h-48 overflow-y-auto space-y-1">
            {clients.map(c=>(
              <button key={c.id} onClick={()=>toggleBulkClient(c.id)}
                className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all"
                style={{background:bulkSelected.includes(c.id)?(isDark?"#0f2e1a":"#f0fdf4"):"transparent"}}>
                <div className="w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 transition-all"
                  style={{background:bulkSelected.includes(c.id)?"#16a34a":"transparent",borderColor:bulkSelected.includes(c.id)?"#16a34a":(isDark?"#475569":"#d1d5db")}}>
                  {bulkSelected.includes(c.id)&&<svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3"><polyline points="20 6 9 17 4 12"/></svg>}
                </div>
                <span className="text-sm font-medium" style={{color:bulkSelected.includes(c.id)?"#16a34a":tc}}>{c.name}</span>
              </button>
            ))}
          </div>
          <div className="flex items-center justify-between pt-1">
            <p className="text-xs" style={{color:mc}}>{bulkSelected.length} selected</p>
            <button onClick={markBulk} disabled={bulkSelected.length===0}
              className="px-5 py-2.5 rounded-xl text-sm font-semibold text-white transition-all"
              style={{background:bulkSelected.length>0?(theme?.primary||"#0f766e"):"#d1d5db"}}>
              Mark Present ✓
            </button>
          </div>
        </div>
      )}
      {clients.length===0?<Empty icon="attendance" message={`Add ${cl(profile,2).toLowerCase()} first.`} theme={theme}/>:(
        <>
          <div className="rounded-2xl p-5 shadow-sm border space-y-4" style={{background:cb,borderColor:bc}}>
            <SD label={`Select ${cl(profile)}`} value={sel?clients.find(c=>c.id===sel)?.name||"":""} onChange={v=>{const c=clients.find(x=>x.name===v);setSel(c?.id||"");}} options={[...clients].sort((a,b)=>a.name.localeCompare(b.name)).map(c=>c.name)} placeholder={`Search ${cl(profile,2).toLowerCase()}...`} theme={theme}/>
            {client&&(
              <>
                <div className="flex gap-3">
                  <div className="flex-1 rounded-xl p-3 text-center" style={{background:theme?.light}}>
                    <p className="text-xl font-black" style={{color:theme?.primary}}>{(()=>{
                      // Use active package startDate (handles packageHistory)
                      const activePkg=getActivePkg(client)||client?.package;
                      const pkgStart=activePkg?.startDate||client?.joiningDate||"2000-01-01";
                      const currentPkgSessions=history.filter(r=>(r.date||"")>=pkgStart).length;
                      // Cap at total sessions if session-based
                      const totalSessions=activePkg?.customClasses||activePkg?.templateSessions||0;
                      if(totalSessions>0)return Math.min(currentPkgSessions,totalSessions);
                      return currentPkgSessions>0?currentPkgSessions:(client?.classesAttended||0);
                    })()}</p>
                    <p className="text-xs" style={{color:theme?.primary}}>Attended</p>
                  </div>
                  {(client.package?.type==="custom"||client.package?.type==="template_sessions")&&<>
                    <div className="flex-1 rounded-xl p-3 text-center" style={{background:"#fffbeb"}}>
                      <p className="text-xl font-black text-amber-700">{client.classesRemaining??0}</p>
                      <p className="text-xs text-amber-500">Remaining</p>
                    </div>
                    <div className="flex-1 rounded-xl p-3 text-center" style={{background:isDark?"#0f172a":"#f9fafb"}}>
                      <p className="text-xl font-black" style={{color:tc}}>{client.package?.customClasses||client.package?.templateSessions||0}</p>
                      <p className="text-xs" style={{color:mc}}>Total</p>
                    </div>
                  </>}
                </div>
                {client.schedule?.length>0&&(
                  <div className="rounded-xl p-3" style={{background:isDark?"#0f172a":"#f8fafc"}}>
                    <p className="text-xs font-semibold mb-2" style={{color:mc}}>Scheduled Classes — tap to mark or pre-fill</p>
                    <div className="space-y-2">
                      {client.schedule.map((s,i)=>{
                        const alreadyMarked=records.find(r=>r.clientId===sel&&r.date===attDate&&r.time===s.time);
                        return(
                          <div key={i} className="flex items-center justify-between rounded-xl px-3 py-2.5"
                            style={{background:alreadyMarked?(isDark?"#14532d":"#dcfce7"):isDark?"#1e293b":"#fff",border:`1px solid ${alreadyMarked?"#16a34a":(isDark?"#334155":"#e5e7eb")}`}}>
                            <div>
                              <p className="text-xs font-semibold" style={{color:alreadyMarked?"#16a34a":(theme?.primary||"#0f766e")}}>{s.days?.join(", ")||s.day} · {s.time}</p>
                              {alreadyMarked&&<p className="text-xs" style={{color:"#16a34a"}}>✓ Marked for {attDate}</p>}
                            </div>
                            {!alreadyMarked&&(
                              <div className="flex gap-1.5">
                                <button onClick={()=>fillSlot(s)}
                                  className="text-xs px-2.5 py-1 rounded-lg font-medium border"
                                  style={{color:mc,borderColor:isDark?"#334155":"#e5e7eb",background:"transparent"}}>
                                  Pre-fill
                                </button>
                                <button onClick={()=>markSlot(s)}
                                  className="text-xs px-2.5 py-1 rounded-lg font-semibold"
                                  style={{background:theme?.primary||"#0f766e",color:"#fff"}}>
                                  ✓ Mark
                                </button>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
                {(()=>{
                  const expiry=packageExpiryOf(client);
                  const sessionExhausted=isSessionBased(client)&&(client.classesRemaining||0)<=0;
                  const timeExpired=expiry&&clientToday()>expiry;
                  const sessionLast=isSessionBased(client)&&(client.classesRemaining||0)===1;
                  const timeExpiring=expiry&&!timeExpired&&expiry<=addDays(clientToday(),3);
                  if(sessionExhausted||timeExpired){
                    return(
                      <div className="rounded-xl p-3 space-y-2" style={{background:"#fef2f2",border:"1px solid #fecaca"}}>
                        <p className="text-sm font-semibold text-red-600">
                          {sessionExhausted?`⛔ Package complete — 0 ${sl(profile,2).toLowerCase()} remaining`:`⛔ Package expired on ${expiry}`}
                        </p>
                        <p className="text-xs text-red-500">Attendance is blocked. Please renew or extend the package.</p>
                        <div className="flex gap-2">
                          <button onClick={()=>{if(onRenewNeeded)onRenewNeeded(client);}}
                            className="flex-1 py-2 rounded-xl text-xs font-semibold text-white"
                            style={{background:"#0f766e"}}>
                            🔄 Renew / Extend
                          </button>
                        </div>
                      </div>
                    );
                  }
                  if(sessionLast||timeExpiring){
                    return(
                      <div className="flex items-center gap-2 p-3 rounded-xl" style={{background:"#fffbeb",border:"1px solid #fde68a"}}>
                        <Icon name="alert" size={16} className="text-amber-500"/>
                        <p className="text-sm text-amber-700 font-semibold">
                          {sessionLast?`1 ${sl(profile,1).toLowerCase()} remaining — time to renew!`:`Package expires on ${expiry} — renew soon!`}
                        </p>
                      </div>
                    );
                  }
                  return null;
                })()}
                <Inp label="Session Note (optional)" value={note} onChange={e=>setNote(e.target.value)} placeholder="e.g. focused on breathing" theme={theme}/>
                <div className="grid grid-cols-2 gap-3 items-end">
                  <Inp label="Date" type="date" value={attDate} onChange={e=>setAttDate(e.target.value)} theme={theme} max={clientToday()}/>
                  {attTime
                    ?<Inp label="Time" type="time" value={attTime} onChange={e=>setAttTime(e.target.value)} theme={theme}/>
                    :<AddTimeBtn onClick={()=>setAttTime(new Date().toTimeString().slice(0,5))} theme={theme}/>
                  }
                </div>
                <Btn onClick={mark} theme={theme} className="w-full justify-center"><Icon name="check" size={16}/>Mark Attendance — {attDate}{attTime?` ${attTime}`:""}</Btn>
              </>
            )}
          </div>
          {sel&&history.length>0&&(
            <div className="rounded-2xl p-5 shadow-sm border" style={{background:cb,borderColor:bc}}>
              <p className="font-semibold text-sm mb-3" style={{color:tc}}>History ({history.length} {history.length===1?"session":"sessions"} total)</p>
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {history.map(r=>(
                  <div key={r.id} className="flex items-center justify-between text-sm py-2" style={{borderTop:"1px dotted "+bc}}>
                    <div>
                      <p className="font-medium" style={{fontVariantNumeric:"tabular-nums",color:tc}}>{r.date}{r.time?" · "+r.time:""}</p>
                      {r.note&&<p className="text-xs" style={{color:mc}}>{r.note}</p>}
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="flex items-center gap-1 text-xs px-2 py-1 rounded-full font-semibold" style={{background:"#dcfce7",color:"#16a34a"}}><Icon name="check" size={10}/>Present</span>
                      <ConfirmButton label={<Icon name="trash" size={13}/>} confirmLabel="Delete?" onConfirm={()=>deleteRec(r.id)} danger className="p-1 rounded-lg" style={{color:"#ef4444",background:"#fef2f2"}}/>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};
